/* eslint-disable @typescript-eslint/no-explicit-any */
import { brand } from '../../../../theme/themePrimitives';
import CustomInput from '../../../reusable/input';
import { RootState } from '../../../../redux/store';
import { useSelector } from 'react-redux';
import {
    Box,
    Button,
    Checkbox,
    Collapse,
    IconButton,
    Paper,
    Popper,
    Typography
} from '@mui/material';
import { Close, ExpandMore } from '@mui/icons-material';
import React, { useState } from 'react';


interface ProspectResult {
    id: number;
    type: string;
    // add other properties if needed
}

interface FilterPanelProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    onApply: (values: {
        location: string;
        searchCategory: number[];
        filters: Record<string, boolean>;
    }) => void;
    appliedLocation: string;
    appliedCategories: number[];
    appliedFilters: Record<string, boolean>;
}

const categories: Record<string, { label: string; value: string }[]> = {
    'Contact Medium': [
        { label: 'Email', value: 'email' },
        { label: 'Phone Number', value: 'phone' },
        { label: 'Website', value: 'website' }
    ],
    'Social Media': [
        { label: 'Facebook', value: 'fb_url' },
        { label: 'Twitter', value: 'twitter_url' },
        { label: 'Linkedin', value: 'linkedin_url' },
        { label: 'Youtube', value: 'youtube_url' },
        { label: 'Instagram', value: 'instagram_url' },
        { label: 'Pinterest', value: 'pinterest_url' },
        { label: 'Tripadvisor', value: 'tripadvisor_url' }
    ],
    'Marketing': [
        { label: 'Mobile Friendly', value: 'is_mobile_friendly' },
        { label: 'Google Claim', value: 'google_claim' },
        { label: 'Facebook Ads', value: 'is_facebook_ads' },
        { label: 'Twitter Ads', value: 'is_twitter_ads' },
        { label: 'LinkedIn Ads', value: 'is_linkedin_ads' },
        { label: 'Bing Ads', value: 'is_bing_ads' },
        { label: 'Google Analytics', value: 'is_ga' },
        { label: 'Use Wordpress', value: 'is_wordpress' },
        { label: 'Use Shopify', value: 'is_shopify' }
    ]
};

const LSFilterPanel: React.FC<FilterPanelProps> = ({
    anchorEl, open, onClose, onApply, appliedCategories, appliedFilters, appliedLocation
}) => {
    const [draftLocation, setDraftLocation] = useState<string>(appliedLocation || '');
    const [draftSearchCategory, setDraftSearchCategory] = useState<number[]>(appliedCategories.map(Number));
    const [draftFilters, setDraftFilters] = useState(appliedFilters);

    const handleApply = () => {
        onClose();
        onApply({
            location: draftLocation,
            searchCategory: draftSearchCategory.map(Number),
            filters: draftFilters
        });
    };

    const onClear = () => {
        setDraftLocation('');
        setDraftSearchCategory([] as number[]);
        setDraftFilters({});
    };

    const { prospectDetail } = useSelector((state: RootState) => state.prospect);
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        'Contact Medium': false,
        'Social Media': false,
        'Marketing': false
    });


    const seen = new Set();
    const searchCategories = prospectDetail?.results
        .filter((result: ProspectResult) => {
            if (!result.type) return false;
            if (seen.has(result.type.toLowerCase())) return false;
            seen.add(result.type.toLowerCase());
            return true;
        })
        .map((result: ProspectResult) => ({
            name: result.type,
            id: result.id
        }));

    const toggleExpand = (category: string) => {
        setExpandedSections((prev) => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    return (
        <Popper open={open} anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 9999 }}>
            <Paper sx={{ p: 2, width: 350, maxHeight: '60vh', overflow: 'auto' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography fontWeight="bold">Advanced Filters</Typography>
                    <Button variant="outlined" color="secondary" size="small" startIcon={<Close />} onClick={onClear}>Clear Filters</Button>
                </Box>

                {/* City Autocomplete */}
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Typography fontSize={12} fontWeight={600}>Location</Typography>
                    <CustomInput
                        type="text"
                        value={draftLocation}
                        onChange={(e) => setDraftLocation(e.target.value as string)}
                        placeholder="Enter Location"
                        fullWidth
                    />
                </Box>
                <Box display="flex" alignItems="center" mb={2}>
                    <Typography fontSize={12} fontWeight={600}>Search Category</Typography>
                    <CustomInput
                        type="autocomplete"
                        value={draftSearchCategory}
                        onChangeForAutoComplete={(newIds) => setDraftSearchCategory(newIds as number[])}
                        options={searchCategories}
                        componentsProps={{ popper: { disablePortal: true }}}
                        placeholder="Choose some options..."
                        fullWidth
                    />
                </Box>
                {Object.entries(categories).map(([category, options]) => (
                    <Box key={category} sx={{ mb: 1 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" bgcolor={brand[50]} color={brand[300]} px="5px" borderRadius={1}>
                            <Typography variant="subtitle2" fontWeight={600}>{category}</Typography>
                            <IconButton
                                size="large"
                                onClick={() => toggleExpand(category)}
                                sx={{ transform: expandedSections[category] ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s', color: brand[300] }}
                            >
                                <ExpandMore fontSize="small" />
                            </IconButton>
                        </Box>
                        <Collapse in={expandedSections[category]} sx={{ bgcolor: brand[20], mt: 1, borderRadius: 1, px: '5px' }}>
                            <Box>
                                {options.map((option) => (
                                    <Box
                                        key={option.value}
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Typography fontSize={12} fontWeight={600}>{option.label}</Typography>
                                        <Checkbox
                                            checked={draftFilters[option.value] || false}
                                            onChange={(e) => setDraftFilters((prev) => ({ ...prev, [option.value]: e.target.checked }))}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Collapse>
                    </Box>
                ))}
                <Box display="flex" alignItems="center" gap={1}>
                    <Button variant="outlined" fullWidth color="secondary" onClick={onClose}>Cancel</Button>
                    <Button variant="contained" fullWidth onClick={handleApply}>Apply</Button>
                </Box>
            </Paper>
        </Popper>
    );
};

export default LSFilterPanel;
